import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Upload, Filter, Search, Download, RefreshCw, Edit, CheckCircle2, Clock, TrendingUp, MoreVertical, Eye, Trash2, ChevronDown, ChevronRight, FileText, Image, Loader, AlertTriangle, Receipt } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { formatDateDisplay } from '../utils/dateUtils';
import { downloadExcel, downloadPDF } from '../utils/downloadHelpers';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { SortButton, DateRangeFilter, SearchFilters, sortData, filterData } from '../components/DataTableWithFilters';
import { usePermissions } from '../utils/permissionUtils';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';
import { WhatsAppIcon } from '../components/WhatsAppButton';

const Salik = ({ user }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks - CRITICAL for security
  const permissions = usePermissions(user, 'salik');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canImport = permissions.canCreateSubPage('import');
  const canBulkUpdate = permissions.canEditSubPage('bulkUpdate');
  
  const [salikRecords, setSalikRecords] = useState([]);
  const [motorcycles, setMotorcycles] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tollReportDialogOpen, setTollReportDialogOpen] = useState(false);
  const [tollReportUploading, setTollReportUploading] = useState(false);
  const [tollReportResult, setTollReportResult] = useState(null);
  const [tollReportMode, setTollReportMode] = useState('skip'); // 'skip' or 'update'
  const [parsedRecords, setParsedRecords] = useState([]); // Store parsed records for re-upload with different mode
  const [editingId, setEditingId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ field: 'salik_date', direction: 'desc' });
  
  // Bulk selection state
  const [selectedRecords, setSelectedRecords] = useState([]);
  
  // ===== Lessee Monthly Summary state =====
  const [lesseeSummary, setLesseeSummary] = useState([]);
  const [lesseeSummaryLoading, setLesseeSummaryLoading] = useState(false);
  const [lesseeSummaryExpanded, setLesseeSummaryExpanded] = useState(false);
  
  // ===== Monthly Audit panel state =====
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [auditData, setAuditData] = useState({ rows: [], totals: {} });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditGroupBy, setAuditGroupBy] = useState('month');
  const [auditYear, setAuditYear] = useState('');
  const [auditMonth, setAuditMonth] = useState('');
  
  // ===== Mark Paid With Proof dialog state =====
  const [paidProofDialog, setPaidProofDialog] = useState({
    open: false, mode: 'single', salikIds: [], lesseeName: '',
    month: '', contextLabel: '', existingProofUrl: '',
  });
  const [paidProofForm, setPaidProofForm] = useState({
    payment_source: 'rider_direct', payment_ref_no: '',
    payment_date: '', payment_time: '', notes: '', replace_reason: '', file: null,
  });
  const [paidProofSubmitting, setPaidProofSubmitting] = useState(false);
  
  // ===== Proof history viewer =====
  const [proofHistoryDialog, setProofHistoryDialog] = useState({ open: false, salikId: '', data: null, loading: false });

  // Function to update SALIK status
  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await axios.patch(
        `${API}/salik/${recordId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Bulk status update
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedRecords.length === 0) {
      toast.error('Please select at least one record');
      return;
    }

    // "Paid" requires proof of payment — open dialog
    if (newStatus === 'Paid') {
      openPaidProofDialog({
        mode: 'bulk',
        salikIds: [...selectedRecords],
        contextLabel: `${selectedRecords.length} selected SALIK record(s)`,
      });
      return;
    }

    try {
      await axios.patch(
        `${API}/salik/bulk-status`,
        { record_ids: selectedRecords, status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`${selectedRecords.length} record(s) updated to ${newStatus}`);
      setSelectedRecords([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Toggle single record selection
  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  // Toggle all records selection
  const toggleSelectAll = () => {
    if (selectedRecords.length === sortedRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(sortedRecords.map(r => r.id));
    }
  };

  // Backfill lessee/rider names
  const handleBackfillNames = async () => {
    try {
      toast.info('Refreshing names... This may take a moment.');
      const response = await axios.post(
        `${API}/salik/backfill-names`,
        {},
        { headers: getAuthHeader() }
      );
      const data = response.data;
      
      // Log full details for debugging
      console.log('Backfill result:', data);
      
      // Build detailed message
      let message = `✅ Updated ${data.updated_count} of ${data.total_salik_records} records.\n\n`;
      
      if (data.skipped_already_complete > 0) {
        message += `⏭️ Skipped ${data.skipped_already_complete} records (already have lessee/rider).\n`;
      }
      
      if (data.records_needing_update > 0 && data.updated_count === 0) {
        message += `ℹ️ ${data.records_needing_update} records needed updates but none matched.\n`;
      }
      
      if (data.not_found_count > 0) {
        message += `⚠️ ${data.not_found_count} plate(s) not found in motorcycles.\n`;
        if (data.not_found_plates && data.not_found_plates.length > 0) {
          message += `   Unmatched plates: ${data.not_found_plates.slice(0, 5).map(p => p.plate).join(', ')}\n`;
        }
      }
      
      if (data.bikes_without_lessee_rider && data.bikes_without_lessee_rider.length > 0) {
        message += `⚠️ ${data.bikes_without_lessee_rider.length} matched bike(s) have no lessee/rider assigned.\n`;
      }
      
      if (data.matched_examples && data.matched_examples.length > 0) {
        message += `\n📋 Matched Examples:\n`;
        data.matched_examples.slice(0, 5).forEach(m => {
          message += `   ${m.salik_plate} → ${m.matched_bike} (${m.match_key})\n`;
          message += `      Lessee: ${m.lessee || 'N/A'}, Rider: ${m.rider || 'N/A'}\n`;
        });
      }
      
      // Add diagnostic info if available
      if (data.diagnostic) {
        message += `\n📊 Diagnostic Info:\n`;
        message += `   Total motorcycles: ${data.diagnostic.total_motorcycles}\n`;
        message += `   With lessee: ${data.diagnostic.motorcycles_with_lessee}\n`;
        message += `   With rider: ${data.diagnostic.motorcycles_with_rider}\n`;
        if (data.diagnostic.sample_motorcycle_plates && data.diagnostic.sample_motorcycle_plates.length > 0) {
          message += `\n   Sample motorcycle plates: ${data.diagnostic.sample_motorcycle_plates.slice(0, 5).join(', ')}\n`;
        }
        if (data.diagnostic.sample_salik_plates && data.diagnostic.sample_salik_plates.length > 0) {
          message += `   Sample Salik plates: ${data.diagnostic.sample_salik_plates.slice(0, 5).join(', ')}\n`;
        }
      }
      
      // Show alert with full details
      alert(message);
      
      if (data.updated_count > 0) {
        toast.success(`Updated ${data.updated_count} records!`);
      } else if (data.not_found_count > 0) {
        toast.warning(`No records updated. ${data.not_found_count} plates not found in motorcycles.`);
      } else {
        toast.info('No records needed updating.');
      }
      
      fetchData();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error(formatErrorMessage(error, 'Failed to refresh names'));
    }
  };

  // Permission check
  const canManage = user?.role !== 'Account Manager';
  
  // Summary filter state (for interactive cards)
  const [summaryFilter, setSummaryFilter] = useState(null); // 'paid', 'unpaid', or null
  const [entryTypeFilter, setEntryTypeFilter] = useState('all'); // 'all' | 'toll' | 'parking'
  const [showBikeRankingDialog, setShowBikeRankingDialog] = useState(false);
  
  // Ranking dialog filter state
  const [rankingPeriod, setRankingPeriod] = useState('all'); // 'all' or 'month'
  const [rankingMonth, setRankingMonth] = useState(new Date().getMonth()); // 0-11
  const [rankingYear, setRankingYear] = useState(new Date().getFullYear());
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    lessee: '',
    rider: '',
    bike: ''
  });
  
  const [formData, setFormData] = useState({
    transaction_id: '',
    salik_date: '',
    salik_time: '',
    bike_plate_number: '',
    bike_id: '',
    lessee_id: '',
    lessee_name: '',
    rider_id: '',
    rider_name: '',
    salik_gate_name: '',
    is_parking: false,
    parking_location: '',
    direction: '',
    tag_number: '',
    amount: 0,
    admin_charge: 0,
    total_amount: 0,
    status: 'Unpaid'
  });

  useEffect(() => {
    fetchData();
  }, []);

  // ===== Lessee Monthly Summary + Monthly Audit handlers =====
  const fetchLesseeSummary = async () => {
    setLesseeSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/salik/lessee-monthly-summary`, { headers: getAuthHeader() });
      setLesseeSummary(res.data.summary || []);
    } catch (e) {
      toast.error('Failed to load lessee summary');
    } finally {
      setLesseeSummaryLoading(false);
    }
  };

  const fetchMonthlyAudit = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ group_by: auditGroupBy });
      if (auditYear) params.append('year', auditYear);
      if (auditMonth) params.append('month', auditMonth);
      const res = await axios.get(`${API}/salik/monthly-audit?${params.toString()}`, { headers: getAuthHeader() });
      setAuditData({ rows: res.data.rows || [], totals: res.data.totals || {} });
    } catch (e) {
      toast.error('Failed to load audit');
    } finally {
      setAuditLoading(false);
    }
  };

  // Send a Salik monthly summary to the rider via WhatsApp using the
  // `salik_summary` Meta template. Called from the audit table when
  // group_by=='rider'. Requires the row to carry rider_id + month_key.
  const handleWhatsAppSalikSummary = async (row) => {
    if (!row?.rider_id || !row?.month_key || row.month_key === 'Unknown') {
      toast.error('Cannot share — rider or month is not identified on this row');
      return;
    }
    const [yy, mm] = row.month_key.split('-');
    if (!window.confirm(`Send Salik summary (${row.label}) via WhatsApp?\n\nTrips: ${row.generated_count}\nTotal: AED ${Number(row.generated_amount).toFixed(2)}`)) return;
    try {
      const params = new URLSearchParams({ year: yy, month: mm });
      if (row.primary_plate) params.append('plate', row.primary_plate);
      const res = await axios.post(
        `${API}/salik/summary/${row.rider_id}/whatsapp?${params.toString()}`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(res.data?.message || 'Salik summary queued via WhatsApp');
    } catch (e) {
      toast.error(formatErrorMessage(e, 'WhatsApp send failed'));
    }
  };

  useEffect(() => {
    if (lesseeSummaryExpanded) fetchLesseeSummary();
    // eslint-disable-next-line
  }, [lesseeSummaryExpanded]);

  useEffect(() => {
    if (auditExpanded) fetchMonthlyAudit();
    // eslint-disable-next-line
  }, [auditExpanded, auditGroupBy, auditYear, auditMonth]);

  // ===== Mark Paid With Proof =====
  const openPaidProofDialog = ({ mode, salikIds = [], lesseeName = '', month = '', contextLabel = '', existingProofUrl = '' }) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const hh = String(today.getHours()).padStart(2, '0');
    const mi = String(today.getMinutes()).padStart(2, '0');
    setPaidProofDialog({
      open: true, mode, salikIds, lesseeName, month, contextLabel,
      existingProofUrl,
    });
    setPaidProofForm({
      payment_source: mode === 'lessee_month' ? 'lessee_claim' : 'rider_direct',
      payment_ref_no: '',
      payment_date: `${yyyy}-${mm}-${dd}`,
      payment_time: `${hh}:${mi}`,
      notes: '', replace_reason: '', file: null,
    });
  };

  const closePaidProofDialog = () => {
    setPaidProofDialog({ open: false, mode: 'single', salikIds: [], lesseeName: '', month: '', contextLabel: '', existingProofUrl: '' });
    setPaidProofForm({ payment_source: 'rider_direct', payment_ref_no: '', payment_date: '', payment_time: '', notes: '', replace_reason: '', file: null });
  };

  const submitPaidProofDialog = async () => {
    const f = paidProofForm;
    const d = paidProofDialog;
    if (!f.payment_source) { toast.error('Select a payment source'); return; }
    if (!f.payment_ref_no || !f.payment_ref_no.trim()) { toast.error('Payment reference number is required'); return; }
    if (!f.payment_date) { toast.error('Payment date is required'); return; }
    if (!f.payment_time) { toast.error('Payment time is required'); return; }
    const replacing = d.mode === 'single' && d.existingProofUrl;
    if (replacing) {
      if (!f.replace_reason || f.replace_reason.trim().length < 5) { toast.error('Replace reason is required (min 5 chars)'); return; }
      if (!f.file) { toast.error('Select a new proof file'); return; }
    } else if (!f.file) {
      toast.error('Proof of payment file is required'); return;
    }
    if (f.file) {
      if (f.file.size > 10 * 1024 * 1024) { toast.error('File exceeds 10 MB limit'); return; }
      const okType = f.file.type.startsWith('image/') || f.file.type === 'application/pdf';
      if (!okType) { toast.error('Only image or PDF files are allowed'); return; }
    }
    setPaidProofSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('proof_file', f.file);
      formData.append('payment_source', f.payment_source);
      formData.append('payment_ref_no', f.payment_ref_no.trim());
      formData.append('payment_date', f.payment_date);
      formData.append('payment_time', f.payment_time);
      if (f.notes) formData.append('notes', f.notes);
      let url;
      if (replacing) {
        url = `${API}/salik/${d.salikIds[0]}/replace-proof`;
        formData.append('replace_reason', f.replace_reason.trim());
      } else if (d.mode === 'lessee_month') {
        url = `${API}/salik/mark-paid-with-proof`;
        formData.append('lessee_name', d.lesseeName);
        formData.append('month', d.month);
      } else {
        url = `${API}/salik/mark-paid-with-proof`;
        formData.append('salik_ids', d.salikIds.join(','));
      }
      const res = await axios.post(url, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message || 'Proof recorded successfully');
      closePaidProofDialog();
      setSelectedRecords([]);
      if (lesseeSummaryExpanded) fetchLesseeSummary();
      if (auditExpanded) fetchMonthlyAudit();
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to mark as paid';
      toast.error(typeof msg === 'string' ? msg : 'Failed to mark as paid');
    } finally {
      setPaidProofSubmitting(false);
    }
  };

  const handleMarkLesseeMonthPaid = (lesseeName, month) => {
    const monthLabel = month !== 'Unknown' ? new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : month;
    openPaidProofDialog({
      mode: 'lessee_month', lesseeName, month,
      contextLabel: `${lesseeName} — ${monthLabel}`,
    });
  };

  const openProofHistory = async (salikId) => {
    setProofHistoryDialog({ open: true, salikId, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/salik/${salikId}/payment-history`, { headers: getAuthHeader() });
      setProofHistoryDialog({ open: true, salikId, data: res.data, loading: false });
    } catch (e) {
      toast.error('Failed to load proof history');
      setProofHistoryDialog({ open: false, salikId: '', data: null, loading: false });
    }
  };
  // ===== End Mark Paid With Proof =====



  const fetchData = async () => {
    try {
      const opts = { headers: getAuthHeader(), timeout: 90000 };
      const [salikRes, bikesRes, lesseesRes, ridersRes] = await Promise.allSettled([
        axios.get(`${API}/salik`, opts),
        axios.get(`${API}/motorcycles/dropdown`, opts),
        axios.get(`${API}/lessees`, opts),
        axios.get(`${API}/riders/dropdown`, opts)
      ]);
      if (salikRes.status === 'fulfilled') {
        setSalikRecords(Array.isArray(salikRes.value.data) ? salikRes.value.data : []);
      } else {
        const err = salikRes.reason;
        toast.error(`Failed to load SALIK records (${err?.response?.status || 'network'}). Please refresh.`);
      }
      if (bikesRes.status === 'fulfilled') setMotorcycles(bikesRes.value.data);
      if (lesseesRes.status === 'fulfilled') setLessees(lesseesRes.value.data);
      if (ridersRes.status === 'fulfilled') setRiders(ridersRes.value.data);
    } finally {
      setLoading(false);
    }
  };

  const handleBikeChange = (bikeId) => {
    const bike = motorcycles.find(m => m.id === bikeId);
    if (bike) {
      // Get lessee info
      const lessee = lessees.find(l => l.id === bike.lessee_id);
      // Get rider info
      const rider = riders.find(r => r.id === bike.current_rider_id);
      
      setFormData({
        ...formData,
        bike_id: bikeId,
        bike_plate_number: bike.plate_number,
        lessee_id: bike.lessee_id || '',
        lessee_name: lessee?.company_name || '',
        rider_id: bike.current_rider_id || '',
        rider_name: rider?.name || ''
      });
    }
  };

  // Auto-calculate admin charge (15%) and total when amount changes
  const handleAmountChange = (newAmount) => {
    const amount = parseFloat(newAmount) || 0;
    const adminCharge = Math.round(amount * 0.15 * 100) / 100;
    const totalAmount = Math.round((amount + adminCharge) * 100) / 100;
    setFormData({
      ...formData,
      amount: amount,
      admin_charge: adminCharge,
      total_amount: totalAmount
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate total before submit
      const submitData = {
        ...formData,
        total_amount: (formData.amount || 0) + (formData.admin_charge || 0)
      };
      
      if (editingId) {
        await axios.put(`${API}/salik/${editingId}`, submitData, { headers: getAuthHeader() });
        toast.success('Salik record updated');
      } else {
        await axios.post(`${API}/salik`, submitData, { headers: getAuthHeader() });
        toast.success('Salik record added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormData({
      transaction_id: record.transaction_id || '',
      salik_date: record.salik_date || record.date || '',
      salik_time: record.salik_time || record.time || '',
      bike_plate_number: record.bike_plate_number || record.motorcycle_plate || '',
      bike_id: record.bike_id || record.motorcycle_id || '',
      lessee_id: record.lessee_id || '',
      lessee_name: record.lessee_name || '',
      rider_id: record.rider_id || '',
      rider_name: record.rider_name || '',
      salik_gate_name: record.salik_gate_name || record.gate_name || '',
      is_parking: !!record.is_parking,
      parking_location: record.parking_location || '',
      direction: record.direction || '',
      tag_number: record.tag_number || '',
      amount: record.amount || 0,
      admin_charge: record.admin_charge || 0,
      total_amount: record.total_amount || 0,
      status: record.status || 'Unpaid'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`${API}/salik/${id}`, { headers: getAuthHeader() });
        toast.success('Salik record deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_id: '',
      salik_date: '',
      salik_time: '',
      bike_plate_number: '',
      bike_id: '',
      lessee_id: '',
      lessee_name: '',
      rider_id: '',
      rider_name: '',
      salik_gate_name: '',
      direction: '',
      tag_number: '',
      amount: 0,
      admin_charge: 0,
      total_amount: 0,
      status: 'Unpaid'
    });
    setEditingId(null);
  };

  // Download toll report template
  const downloadTollReportTemplate = () => {
    import('xlsx').then((XLSX) => {
      const template = [
        {
          'Transaction ID': '50293588965',
          'Trip Date': '04 Jan 2026',
          'Trip Time': '11:01:12 PM',
          'Transaction Post Date': '05 Jan 2026',
          'Toll Gate': 'Al Maktoum Bridge',
          'Direction': 'Abu Dhabi',
          'Tag Number': '12955521',
          'Plate': '80452',
          'Amount': 4.0
        },
        {
          'Transaction ID': '70191988509',
          'Trip Date': '04 Jan 2026',
          'Trip Time': '04:30:07 AM',
          'Transaction Post Date': '05 Jan 2026',
          'Toll Gate': 'Al Safa North',
          'Direction': 'Sharjah',
          'Tag Number': '11556882',
          'Plate': '43203',
          'Amount': 4.0
        }
      ];

      const worksheet = XLSX.utils.json_to_sheet(template);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'SALIK Toll Report');
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 15 }, // Transaction ID
        { wch: 15 }, // Trip Date
        { wch: 15 }, // Trip Time
        { wch: 20 }, // Transaction Post Date
        { wch: 20 }, // Toll Gate
        { wch: 12 }, // Direction
        { wch: 12 }, // Tag Number
        { wch: 10 }, // Plate
        { wch: 12 }  // Amount
      ];

      XLSX.writeFile(workbook, 'SALIK_Toll_Report_Template.xlsx');
      toast.success('Template downloaded successfully');
    });
  };

  const handleTollReportUpload = async (file) => {
    if (!file) return;
    
    setTollReportUploading(true);
    setTollReportResult(null);
    
    try {
      // Parse the Excel file
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Convert to JSON - skip header rows, look for the actual data
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Find the header row (contains "Transaction ID")
      let headerRowIndex = -1;
      for (let i = 0; i < Math.min(20, jsonData.length); i++) {
        const row = jsonData[i];
        if (row && row.some(cell => String(cell).includes('Transaction ID'))) {
          headerRowIndex = i;
          break;
        }
      }
      
      if (headerRowIndex === -1) {
        throw new Error('Could not find header row with "Transaction ID" column');
      }
      
      // Extract headers
      const headers = jsonData[headerRowIndex].map(h => String(h || '').trim().toLowerCase());
      console.log('Found headers:', headers);
      
      // Map column indices
      const colMap = {
        transaction_id: headers.findIndex(h => h.includes('transaction') && h.includes('id')),
        trip_date: headers.findIndex(h => h.includes('trip') && h.includes('date')),
        trip_time: headers.findIndex(h => h.includes('trip') && h.includes('time')),
        toll_gate: headers.findIndex(h => h.includes('toll') && h.includes('gate')),
        direction: headers.findIndex(h => h === 'direction'),
        tag_number: headers.findIndex(h => h.includes('tag') && h.includes('number')),
        plate: headers.findIndex(h => h === 'plate'),
        amount: headers.findIndex(h => h.includes('amount'))
      };
      
      console.log('Column mapping:', colMap);
      
      // Helper: convert Excel serial date to DD-MM-YYYY
      const excelDateToString = (value) => {
        if (!value) return '';
        // If it's already a string date, return as-is
        if (typeof value === 'string' && value.match(/[a-zA-Z]/)) return value;
        // If it's a number (Excel serial date), convert it
        const num = Number(value);
        if (!isNaN(num) && num > 40000 && num < 60000) {
          // Excel serial date: days since 1900-01-01 (with Excel's leap year bug)
          const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
          const date = new Date(excelEpoch.getTime() + num * 86400000);
          const dd = String(date.getDate()).padStart(2, '0');
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const yyyy = date.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
        }
        return String(value);
      };

      // Helper: convert Excel serial time to HH:MM:SS AM/PM
      const excelTimeToString = (value) => {
        if (!value) return '';
        if (typeof value === 'string' && value.includes(':')) return value;
        const num = Number(value);
        if (!isNaN(num) && num >= 0 && num < 1) {
          const totalSeconds = Math.round(num * 86400);
          let hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${ampm}`;
        }
        return String(value);
      };

      // Parse data rows
      const records = [];
      for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;
        
        // Skip empty rows
        const transactionId = row[colMap.transaction_id];
        if (!transactionId) continue;
        
        records.push({
          transaction_id: String(transactionId || '').replace(/\.0$/, ''),
          trip_date: excelDateToString(row[colMap.trip_date]),
          trip_time: excelTimeToString(row[colMap.trip_time]),
          toll_gate: row[colMap.toll_gate] || '',
          direction: row[colMap.direction] || '',
          tag_number: String(row[colMap.tag_number] || '').replace(/\.0$/, ''),
          plate: String(row[colMap.plate] || '').replace(/\.0$/, ''),
          amount_aed: row[colMap.amount] || 0
        });
      }
      
      console.log(`Parsed ${records.length} records from toll report`);
      
      if (records.length === 0) {
        throw new Error('No valid records found in the file');
      }
      
      // Store parsed records for potential re-upload with different mode
      setParsedRecords(records);
      
      // Upload to backend with selected mode
      const response = await axios.post(
        `${API}/salik/import-toll-report`,
        { records, mode: tollReportMode },
        { headers: getAuthHeader() }
      );
      
      setTollReportResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`✅ Imported ${response.data.success_count} SALIK records!`);
      }
      if (response.data.updated_count > 0) {
        toast.success(`🔄 Updated ${response.data.updated_count} existing records!`);
      }
      if (response.data.skipped_count > 0) {
        toast.info(`⏭️ Skipped ${response.data.skipped_count} duplicate records`);
      }
      if (response.data.failed_count > 0) {
        toast.warning(`⚠️ ${response.data.failed_count} records failed`);
      }
      
      fetchData();
      
    } catch (error) {
      console.error('Toll report upload error:', error);
      toast.error(error.message || 'Failed to upload toll report');
      setTollReportResult({ error: error.message });
    } finally {
      setTollReportUploading(false);
    }
  };

  // Re-upload with different mode
  const handleReUploadWithMode = async (newMode) => {
    if (parsedRecords.length === 0) {
      toast.error('No records to re-upload. Please select a file first.');
      return;
    }
    
    setTollReportUploading(true);
    setTollReportMode(newMode);
    
    try {
      const response = await axios.post(
        `${API}/salik/import-toll-report`,
        { records: parsedRecords, mode: newMode },
        { headers: getAuthHeader() }
      );
      
      setTollReportResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`✅ Imported ${response.data.success_count} SALIK records!`);
      }
      if (response.data.updated_count > 0) {
        toast.success(`🔄 Updated ${response.data.updated_count} existing records!`);
      }
      if (response.data.skipped_count > 0) {
        toast.info(`⏭️ Skipped ${response.data.skipped_count} duplicate records`);
      }
      if (response.data.failed_count > 0) {
        toast.warning(`⚠️ ${response.data.failed_count} records failed`);
      }
      
      fetchData();
      
    } catch (error) {
      console.error('Re-upload error:', error);
      toast.error(error.message || 'Failed to re-upload');
    } finally {
      setTollReportUploading(false);
    }
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDetailDialogOpen(true);
  };

  const salikDownloadColumns = [
    { header: 'Transaction ID', key: 'transaction_id', width: 20 },
    { header: 'Date', key: 'salik_date', width: 12, format: (v, row) => v || row.date || '-' },
    { header: 'Time', key: 'salik_time', width: 10, format: (v, row) => v || row.time || '-' },
    { header: 'Plate', key: 'bike_plate_number', width: 15, format: (v, row) => v || row.motorcycle_plate || '-' },
    { header: 'Lessee', key: 'lessee_name', width: 22, format: (v, row) => v || row.lessee_company_name || '-' },
    { header: 'Current Rider', key: 'current_rider_name', width: 22 },
    { header: 'SALIK by', key: 'salik_by_rider_name', width: 22, format: (v, row) => v || row.rider_name || '-' },
    { header: 'Type', key: 'is_parking', width: 10, format: (v) => v ? 'Parking' : 'Toll' },
    { header: 'Toll Gate / Parking Location', key: '_gate', width: 30,
      format: (_, row) => row.is_parking
        ? (row.parking_location || row.salik_gate_name || row.gate_name || '-')
        : (row.salik_gate_name || row.gate_name || '-') },
    { header: 'SALIK (Dh)', key: 'amount', width: 12, format: (v) => (Number(v) || 0).toFixed(2) },
    { header: 'Admin (Dh)', key: 'admin_charge', width: 12,
      format: (v, row) => Number(v ?? (row.amount || 0) * 0.15).toFixed(2) },
    { header: 'Total (Dh)', key: 'total_amount', width: 12,
      format: (v, row) => Number(v ?? ((row.amount || 0) + (row.admin_charge ?? (row.amount || 0) * 0.15))).toFixed(2) },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Payment Source', key: 'payment_source', width: 18, format: (v) => v || '-' },
    { header: 'Payment Ref', key: 'payment_ref_no', width: 18, format: (v) => v || '-' },
    { header: 'Payment Date', key: 'payment_date', width: 12, format: (v) => v || '-' },
  ];

  // Compose a human-readable summary of every filter currently in effect —
  // shown as the PDF header subtitle and in the download toast so operators
  // can tell at a glance what a downloaded file actually represents.
  const buildSalikFilterInfo = () => {
    const parts = [];
    if (filters.fromDate) parts.push(`From: ${filters.fromDate}`);
    if (filters.toDate) parts.push(`To: ${filters.toDate}`);
    if (filters.lessee) parts.push(`Lessee: ${filters.lessee}`);
    if (filters.rider) parts.push(`Rider: ${filters.rider}`);
    if (filters.bike) parts.push(`Bike: ${filters.bike}`);
    if (summaryFilter) parts.push(`Status: ${summaryFilter === 'paid' ? 'Paid' : 'Unpaid'}`);
    if (entryTypeFilter && entryTypeFilter !== 'all') {
      parts.push(`Type: ${entryTypeFilter === 'parking' ? 'Parking-through-Salik' : 'Toll'}`);
    }
    return parts.length ? parts.join(' | ') : 'All records (no filters applied)';
  };

  const handleDownloadSalik = (format) => {
    try {
      const data = sortedRecords || displayRecords || salikRecords;
      if (!data || data.length === 0) {
        toast.error('No records to download');
        return;
      }
      const filterInfo = buildSalikFilterInfo();
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `SALIK_Log_${stamp}${entryTypeFilter && entryTypeFilter !== 'all' ? `_${entryTypeFilter}` : ''}`;
      toast.info(`Preparing ${format.toUpperCase()} for ${data.length} record(s)…`);

      if (format === 'excel') {
        downloadExcel(data, salikDownloadColumns, filename, 'SALIK');
      } else {
        downloadPDF(data, salikDownloadColumns, filename, `SALIK Log — ${data.length} record(s)`, filterInfo);
      }
      toast.success(`Downloaded ${data.length} record(s) · ${filterInfo}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96">Loading...</div>;

  // Apply filters and sorting
  const filteredRecords = filterData(salikRecords, filters);
  
  // Apply summary filter (paid/unpaid)
  let displayRecords = filteredRecords;
  if (summaryFilter === 'paid') {
    displayRecords = filteredRecords.filter(r => r.status === 'Paid');
  } else if (summaryFilter === 'unpaid') {
    displayRecords = filteredRecords.filter(r => r.status !== 'Paid');
  }

  // Apply toll/parking type filter
  if (entryTypeFilter === 'parking') {
    displayRecords = displayRecords.filter(r => !!r.is_parking);
  } else if (entryTypeFilter === 'toll') {
    displayRecords = displayRecords.filter(r => !r.is_parking);
  }
  
  const sortedRecords = sortData(displayRecords, sortConfig.field, sortConfig.direction);

  // Calculate summary statistics
  const paidRecords = salikRecords.filter(r => r.status === 'Paid');
  const unpaidRecords = salikRecords.filter(r => r.status !== 'Paid');
  
  const totalPaid = paidRecords.reduce((sum, r) => sum + (r.total_amount || (r.amount || 0) * 1.15), 0);
  const totalUnpaid = unpaidRecords.reduce((sum, r) => sum + (r.total_amount || (r.amount || 0) * 1.15), 0);
  
  // Get current month date range for display
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const currentMonthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });
  
  // Filter records for current month (for highest usage card display)
  const currentMonthRecords = salikRecords.filter(r => {
    const recordDate = new Date(r.salik_date || r.date);
    return recordDate >= currentMonthStart && recordDate <= now;
  });
  
  // Find motorcycle usage for CURRENT MONTH (for summary card)
  const currentMonthBikeUsage = {};
  currentMonthRecords.forEach(r => {
    const plate = r.bike_plate_number || r.motorcycle_plate || 'Unknown';
    if (!currentMonthBikeUsage[plate]) {
      currentMonthBikeUsage[plate] = { plate, count: 0, total: 0 };
    }
    currentMonthBikeUsage[plate].count += 1;
    currentMonthBikeUsage[plate].total += (r.total_amount || (r.amount || 0) * 1.15);
  });
  
  // Sort current month bikes by total (for summary card)
  const sortedCurrentMonthBikeUsage = Object.values(currentMonthBikeUsage).sort((a, b) => b.total - a.total);
  let highestBike = sortedCurrentMonthBikeUsage[0] || { plate: '-', count: 0, total: 0 };
  
  // Find motorcycle usage for ALL TIME (for ranking dialog)
  const allTimeBikeUsage = {};
  salikRecords.forEach(r => {
    const plate = r.bike_plate_number || r.motorcycle_plate || 'Unknown';
    const lesseeName = r.lessee_name || r.lessee_company_name || '-';
    const riderName = r.rider_name || '-';
    if (!allTimeBikeUsage[plate]) {
      allTimeBikeUsage[plate] = { plate, count: 0, total: 0, lessee: lesseeName, rider: riderName };
    }
    allTimeBikeUsage[plate].count += 1;
    allTimeBikeUsage[plate].total += (r.total_amount || (r.amount || 0) * 1.15);
    // Update lessee/rider if found
    if (lesseeName !== '-') allTimeBikeUsage[plate].lessee = lesseeName;
    if (riderName !== '-') allTimeBikeUsage[plate].rider = riderName;
  });
  
  // Sort all-time bikes by total amount (highest to lowest)
  const sortedAllTimeBikeUsage = Object.values(allTimeBikeUsage).sort((a, b) => b.total - a.total);
  
  const summaryStats = {
    paidCount: paidRecords.length,
    unpaidCount: unpaidRecords.length,
    totalPaid,
    totalUnpaid,
    highestBike,
    sortedBikeUsage: sortedAllTimeBikeUsage, // For dialog - ALL TIME data
    currentMonthName
  };

  const handleSort = (field, direction) => {
    setSortConfig({ field, direction });
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="salik-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('salik.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('salik.subtitle')}</p>
        </div>
        {user?.role !== 'Account Manager' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-lime-600 to-green-600" size={isMobile ? "sm" : "default"} data-testid="add-salik-btn">
                <Plus className="w-4 h-4 mr-2" />
                {isMobile ? 'Add' : t('salik.addSalik')}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? t('salik.editSalik') : t('salik.addSalik')}</DialogTitle>
              <DialogDescription>
                {t('salik.subtitle')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Transaction ID */}
              <div className="space-y-2">
                <Label>Transaction ID *</Label>
                <Input
                  type="text"
                  value={formData.transaction_id}
                  onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                  placeholder="Enter transaction ID"
                  required
                  data-testid="salik-transaction-id"
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.salik_date}
                    onChange={(e) => setFormData({ ...formData, salik_date: e.target.value })}
                    required
                    data-testid="salik-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <Input
                    type="time"
                    value={formData.salik_time}
                    onChange={(e) => setFormData({ ...formData, salik_time: e.target.value })}
                    required
                    data-testid="salik-time"
                  />
                </div>
              </div>

              {/* Motorcycle Selection */}
              <div className="space-y-2">
                <Label>Motorcycle *</Label>
                <SearchableSelect
                  options={motorcycles.map((bike) => ({
                    value: bike.id,
                    label: bike.plate_number,
                    searchText: `${bike.plate_number} ${bike.model_name} ${bike.chassis_number || ''}`,
                    metadata: {
                      'Model': bike.model_name,
                      'Chassis': bike.chassis_number,
                      'City': bike.city || '-'
                    }
                  }))}
                  value={formData.bike_id}
                  onValueChange={handleBikeChange}
                  placeholder="Select motorcycle"
                  emptyMessage="No motorcycles found"
                />
              </div>

              {/* Auto-populated Lessee and Rider Info */}
              {formData.bike_id && (
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Lessee Company</Label>
                    <div className="font-medium text-slate-800">{formData.lessee_name || 'Not assigned'}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Rider Name</Label>
                    <div className="font-medium text-slate-800">{formData.rider_name || 'Not assigned'}</div>
                  </div>
                </div>
              )}

              {/* Parking through Salik toggle — flips the block below
                  from Toll Gate/Direction (both mandatory) to a free-text
                  Parking Location description (Toll Gate + Direction cleared
                  and non-mandatory when checked). */}
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                <input
                  id="salik-is-parking"
                  type="checkbox"
                  checked={!!formData.is_parking}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_parking: e.target.checked,
                    // Clear the mandatory fields so validation passes and
                    // stale values don't leak into the saved record.
                    salik_gate_name: e.target.checked ? '' : formData.salik_gate_name,
                    direction: e.target.checked ? '' : formData.direction,
                    parking_location: e.target.checked ? formData.parking_location : '',
                  })}
                  className="h-4 w-4 accent-emerald-600"
                  data-testid="salik-is-parking-checkbox"
                />
                <Label htmlFor="salik-is-parking" className="cursor-pointer text-sm text-emerald-800">
                  Parking through Salik
                </Label>
              </div>

              {formData.is_parking ? (
                <div className="space-y-2">
                  <Label>Parking Location *</Label>
                  <Input
                    type="text"
                    value={formData.parking_location}
                    onChange={(e) => setFormData({ ...formData, parking_location: e.target.value })}
                    placeholder='e.g. "DSO Street Parking - Phase 1- DXB (DSO Street Parking - Phase 1- DXB)"'
                    required
                    data-testid="salik-parking-location"
                  />
                  <span className="text-xs text-slate-500">
                    This description will appear under the Toll Gate column on the log.
                  </span>
                </div>
              ) : (
                /* Toll Gate and Direction */
                <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Toll Gate *</Label>
                  <SearchableSelect
                    options={[
                      { value: 'Al Maktoum Bridge', label: 'Al Maktoum Bridge' },
                      { value: 'Al Garhoud Bridge', label: 'Al Garhoud Bridge' },
                      { value: 'Business Bay Crossing', label: 'Business Bay Crossing' },
                      { value: 'Al Safa South', label: 'Al Safa South' },
                      { value: 'Al Safa North', label: 'Al Safa North' },
                      { value: 'Airport Tunnel', label: 'Airport Tunnel' },
                      { value: 'Al Mamzar', label: 'Al Mamzar' },
                      { value: 'Al Barsha South', label: 'Al Barsha South' }
                    ]}
                    value={formData.salik_gate_name}
                    onValueChange={(value) => setFormData({ ...formData, salik_gate_name: value })}
                    placeholder="Select gate"
                    showMetadata={false}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Direction *</Label>
                  <Select 
                    value={formData.direction} 
                    onValueChange={(value) => setFormData({ ...formData, direction: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select direction" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                      <SelectItem value="Sharjah">Sharjah</SelectItem>
                      <SelectItem value="Dubai">Dubai</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                </div>
              )}

              {/* Tag Number */}
              <div className="space-y-2">
                <Label>Tag Number *</Label>
                <Input
                  type="text"
                  value={formData.tag_number}
                  onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })}
                  placeholder="SALIK tag number"
                  required
                  data-testid="salik-tag-number"
                />
              </div>

              {/* Amount Fields */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>SALIK Amount (Dh) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    required
                    data-testid="salik-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Admin Charge (Dh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.admin_charge}
                    readOnly
                    className="bg-slate-100"
                    data-testid="salik-admin-charge"
                  />
                  <span className="text-xs text-slate-500">Auto-calculated (15%)</span>
                </div>
                <div className="space-y-2">
                  <Label>Total (Dh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    readOnly
                    className="bg-slate-100 font-bold"
                    data-testid="salik-total"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className={formData.status === 'Paid' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Unpaid" className="text-red-700">Unpaid</SelectItem>
                    <SelectItem value="Paid" className="text-green-700">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Total Amount Display */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Total Amount:</span>
                  <span className="text-2xl font-bold text-purple-700">
                    <CurrencyDisplay amount={formData.total_amount || ((formData.amount || 0) + (formData.admin_charge || 0))} />
                  </span>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-purple-600 to-violet-600" data-testid="submit-salik-btn">
                  {editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Toll Report Import Dialog */}
      <Dialog open={tollReportDialogOpen} onOpenChange={setTollReportDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Import SALIK Toll Report
            </DialogTitle>
            <DialogDescription>
              Upload the Excel file (.xls/.xlsx) exported directly from the SALIK toll provider portal.
              The system will automatically match plate numbers and import the records.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
              <div className="font-medium text-purple-800 mb-2">Expected columns:</div>
              <ul className="list-disc list-inside text-purple-700 space-y-1">
                <li>Transaction ID</li>
                <li>Trip Date & Trip Time</li>
                <li>Toll Gate & Direction</li>
                <li>Tag Number & Plate (last 5 digits)</li>
                <li>Amount</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={downloadTollReportTemplate}
                className="w-full gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                <Download className="w-4 h-4" />
                Download Template
              </Button>
              
              <div className="space-y-2">
                <Label>How to handle duplicate entries?</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="skip"
                      checked={tollReportMode === 'skip'}
                      onChange={(e) => setTollReportMode(e.target.value)}
                      className="text-purple-600"
                    />
                    <span className="text-sm">Skip duplicates</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="update"
                      checked={tollReportMode === 'update'}
                      onChange={(e) => setTollReportMode(e.target.value)}
                      className="text-purple-600"
                    />
                    <span className="text-sm">Update existing</span>
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tollReportFile">Select Toll Report File</Label>
                <Input
                  id="tollReportFile"
                  type="file"
                  accept=".xls,.xlsx"
                  disabled={tollReportUploading}
                  onChange={(e) => handleTollReportUpload(e.target.files[0])}
                  className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200"
                />
              </div>
            </div>

            {tollReportUploading && (
              <div className="flex items-center gap-2 text-purple-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                Processing toll report...
              </div>
            )}

            {tollReportResult && !tollReportResult.error && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-green-700">{tollReportResult.success_count}</div>
                    <div className="text-xs text-green-600">New</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-blue-700">{tollReportResult.updated_count || 0}</div>
                    <div className="text-xs text-blue-600">Updated</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-yellow-700">{tollReportResult.skipped_count || 0}</div>
                    <div className="text-xs text-yellow-600">Skipped</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-red-700">{tollReportResult.failed_count}</div>
                    <div className="text-xs text-red-600">Failed</div>
                  </div>
                </div>

                {/* Show option to re-upload with different mode if there were skipped records */}
                {tollReportResult.skipped_count > 0 && tollReportMode === 'skip' && parsedRecords.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm text-blue-800 mb-2">
                      {tollReportResult.skipped_count} duplicate records were skipped. Would you like to update them instead?
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReUploadWithMode('update')}
                      disabled={tollReportUploading}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      Update Existing Records
                    </Button>
                  </div>
                )}

                {tollReportResult.errors && tollReportResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div className="font-semibold text-red-800 text-sm mb-1">Errors:</div>
                    <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                      {tollReportResult.errors.slice(0, 10).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {tollReportResult.errors.length > 10 && (
                        <li>... and {tollReportResult.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {tollReportResult?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-700 text-sm">{tollReportResult.error}</div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setTollReportDialogOpen(false);
              setTollReportResult(null);
              setParsedRecords([]);
              setTollReportMode('skip');
            }}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Paid Summary */}
        <Card 
          className={`border-0 shadow-md cursor-pointer transition-all hover:shadow-lg ${
            summaryFilter === 'paid' 
              ? 'ring-2 ring-green-500 bg-green-100' 
              : 'bg-gradient-to-br from-green-50 to-green-100'
          }`}
          onClick={() => setSummaryFilter(summaryFilter === 'paid' ? null : 'paid')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-700">
                  <CurrencyDisplay amount={summaryStats.totalPaid} />
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {summaryStats.paidCount} record(s) {summaryFilter === 'paid' && '• Click to clear filter'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-200 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unpaid Summary */}
        <Card 
          className={`border-0 shadow-md cursor-pointer transition-all hover:shadow-lg ${
            summaryFilter === 'unpaid' 
              ? 'ring-2 ring-red-500 bg-red-100' 
              : 'bg-gradient-to-br from-red-50 to-red-100'
          }`}
          onClick={() => setSummaryFilter(summaryFilter === 'unpaid' ? null : 'unpaid')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Total Unpaid</p>
                <p className="text-2xl font-bold text-red-700">
                  <CurrencyDisplay amount={summaryStats.totalUnpaid} />
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {summaryStats.unpaidCount} record(s) {summaryFilter === 'unpaid' && '• Click to clear filter'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-200 flex items-center justify-center">
                <Clock className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Highest SALIK Usage */}
        <Card 
          className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-purple-100 cursor-pointer transition-all hover:shadow-lg"
          onClick={() => setShowBikeRankingDialog(true)}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">
                  Highest SALIK Usage
                  <span className="text-xs ml-1 opacity-75">({summaryStats.currentMonthName})</span>
                </p>
                <p className="text-lg font-bold text-purple-700 truncate" title={summaryStats.highestBike.plate}>
                  {summaryStats.highestBike.plate}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {summaryStats.highestBike.count} trip(s) • <CurrencyDisplay amount={summaryStats.highestBike.total} />
                  <span className="ml-1 opacity-75">• Click to see all</span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-200 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toll / Parking type toggle */}
      <div className="flex items-center gap-2" data-testid="salik-entry-type-toggle">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Entry Type:</span>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {[
            { key: 'all', label: 'All', count: salikRecords.length },
            { key: 'toll', label: 'Toll', count: salikRecords.filter(r => !r.is_parking).length },
            { key: 'parking', label: 'Parking', count: salikRecords.filter(r => !!r.is_parking).length },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              data-testid={`salik-entry-type-${opt.key}`}
              onClick={() => setEntryTypeFilter(opt.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                entryTypeFilter === opt.key
                  ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {opt.label} <span className="ml-1 text-slate-400">({opt.count})</span>
            </button>
          ))}
        </div>
        {entryTypeFilter !== 'all' && (
          <span className="text-xs text-slate-500">
            Showing <strong>{entryTypeFilter === 'parking' ? 'parking-through-Salik' : 'toll gate'}</strong> rows only
          </span>
        )}
      </div>

      {/* Active Filter Indicator */}
      {summaryFilter && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-blue-700">
            Showing: <strong>{summaryFilter === 'paid' ? 'Paid' : 'Unpaid'}</strong> records only
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSummaryFilter(null)}
            className="h-6 px-2 text-blue-600 hover:text-blue-800"
          >
            Clear Filter
          </Button>
        </div>
      )}

      {/* ===== Lessee Monthly Summary Panel ===== */}
      <Card data-testid="salik-lessee-summary-card">
        <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setLesseeSummaryExpanded(!lesseeSummaryExpanded)} data-testid="salik-lessee-summary-toggle">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              {lesseeSummaryExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Receipt className="w-5 h-5 text-blue-600" />
              Lessee-wise Monthly Summary
            </span>
            <span className="text-xs text-slate-500 font-normal">
              {lesseeSummaryExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </CardTitle>
        </CardHeader>
        {lesseeSummaryExpanded && (
          <CardContent>
            {lesseeSummaryLoading ? (
              <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : lesseeSummary.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No SALIK records to summarise yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Lessee · Month</TableHead>
                      <TableHead className="text-right text-xs">Count</TableHead>
                      <TableHead className="text-right text-xs">Total (AED)</TableHead>
                      <TableHead className="text-right text-xs">Admin (15%)</TableHead>
                      <TableHead className="text-right text-xs text-green-700 whitespace-normal break-words leading-tight align-bottom min-w-[120px]" title="Sum of SALIK records recovered from rider salary in approved payroll batches">Recovered (Rider&rsquo;s Salary)</TableHead>
                      <TableHead className="text-right text-xs text-teal-700">Paid by Rider</TableHead>
                      <TableHead className="text-center text-xs">Status</TableHead>
                      <TableHead className="text-center text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lesseeSummary.map((g, idx) => {
                      const monthLabel = g.month !== 'Unknown' ? new Date(g.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : g.month;
                      return (
                        <TableRow key={`${g.lessee_name}-${g.month}-${idx}`} className="hover:bg-slate-50">
                          <TableCell className="text-xs">
                            <div className="font-medium">{g.lessee_name}</div>
                            <div className="text-[10px] text-slate-500">{monthLabel}</div>
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">{g.count}</TableCell>
                          <TableCell className="text-right text-xs font-mono">{Number(g.total_amount).toFixed(2)}</TableCell>
                          <TableCell className="text-right text-xs font-mono text-slate-600">{Number(g.admin_charges).toFixed(2)}</TableCell>
                          <TableCell className="text-right text-xs font-mono text-green-700">{g.recovered_from_salary > 0 ? Number(g.recovered_from_salary).toFixed(2) : <span className="text-slate-300">—</span>}</TableCell>
                          <TableCell className="text-right text-xs font-mono text-teal-700">{g.paid_by_rider_direct > 0 ? Number(g.paid_by_rider_direct).toFixed(2) : <span className="text-slate-300">—</span>}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={
                              g.status === 'paid' ? 'border-green-300 text-green-700 bg-green-50 text-[10px]' :
                              g.status === 'partial' ? 'border-amber-300 text-amber-700 bg-amber-50 text-[10px]' :
                              'border-slate-300 text-slate-600 bg-slate-50 text-[10px]'
                            }>{g.status}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {g.status !== 'paid' && canEdit && (
                              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleMarkLesseeMonthPaid(g.lessee_name, g.month)} data-testid={`salik-mark-paid-${idx}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ===== Monthly SALIK Audit Panel ===== */}
      <Card data-testid="salik-audit-card">
        <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setAuditExpanded(!auditExpanded)} data-testid="salik-audit-toggle">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              {auditExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Monthly SALIK Audit
            </span>
            <span className="text-xs text-slate-500 font-normal">
              {auditExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </CardTitle>
        </CardHeader>
        {auditExpanded && (
          <CardContent className="space-y-3">
            {/* Filter row */}
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Group By</Label>
                <Select value={auditGroupBy} onValueChange={setAuditGroupBy}>
                  <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="lessee">Lessee · Month</SelectItem>
                    <SelectItem value="rider">Rider · Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Year</Label>
                <Input type="number" value={auditYear} onChange={(e) => setAuditYear(e.target.value)} placeholder="e.g. 2026" className="h-8 w-24 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Month</Label>
                <Input type="number" min="1" max="12" value={auditMonth} onChange={(e) => setAuditMonth(e.target.value)} placeholder="1-12" className="h-8 w-20 text-xs" />
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setAuditYear(''); setAuditMonth(''); }}>Reset</Button>
            </div>

            {/* KPI strip */}
            {!auditLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {[
                  { label: 'Generated', val: auditData.totals.generated, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                  { label: 'Claimed to Lessee', val: auditData.totals.claimed, color: 'bg-purple-50 border-purple-200 text-purple-700' },
                  { label: "Recovered (Rider's Salary)", val: auditData.totals.recovered, color: 'bg-green-50 border-green-200 text-green-700' },
                  { label: 'Paid by Rider', val: auditData.totals.rider_paid, color: 'bg-teal-50 border-teal-200 text-teal-700' },
                  { label: 'Pending', val: auditData.totals.pending, color: 'bg-amber-50 border-amber-200 text-amber-800' },
                  { label: 'Admin (15%)', val: auditData.totals.admin, color: 'bg-slate-50 border-slate-200 text-slate-700' },
                ].map(c => (
                  <div key={c.label} className={`rounded-md border ${c.color} px-3 py-2`}>
                    <p className="text-[10px] uppercase tracking-tight font-medium whitespace-normal break-words leading-snug min-h-[28px]">{c.label}</p>
                    <p className="text-lg font-bold">AED {Number(c.val || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Table */}
            {auditLoading ? (
              <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : auditData.rows.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No SALIK records match the selected filter.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="whitespace-nowrap">{auditGroupBy === 'month' ? 'Month' : auditGroupBy === 'lessee' ? 'Lessee · Month' : 'Rider · Month'}</TableHead>
                      <TableHead className="text-right">Generated</TableHead>
                      <TableHead className="text-right text-purple-700 whitespace-normal break-words leading-tight align-bottom min-w-[110px]">Claimed to Lessee</TableHead>
                      <TableHead className="text-right text-green-700 whitespace-normal break-words leading-tight align-bottom min-w-[120px]">Recovered (Rider&rsquo;s Salary)</TableHead>
                      <TableHead className="text-right text-teal-700 whitespace-normal break-words leading-tight align-bottom min-w-[90px]">Rider Paid</TableHead>
                      <TableHead className="text-right text-amber-700">Pending</TableHead>
                      <TableHead className="text-right">Admin Chg.</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      {auditGroupBy === 'rider' && (
                        <TableHead className="text-center whitespace-nowrap">Share</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData.rows.map((r) => (
                      <TableRow key={r.key} className="hover:bg-slate-50">
                        <TableCell className="font-medium text-sm">{r.label}</TableCell>
                        <TableCell className="text-right font-mono text-sm">AED {Number(r.generated_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[10px] text-slate-400">({r.generated_count})</span></TableCell>
                        <TableCell className="text-right font-mono text-sm text-purple-700">{r.claimed_amount > 0 ? `AED ${Number(r.claimed_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-700">{r.recovered_salary_amount > 0 ? `AED ${Number(r.recovered_salary_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-teal-700">{r.rider_paid_amount > 0 ? `AED ${Number(r.rider_paid_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-amber-700">{r.pending_amount > 0 ? `AED ${Number(r.pending_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-500">AED {Number(r.admin_charges).toFixed(2)}</TableCell>
                        <TableCell className={`text-right font-mono text-xs ${Math.abs(r.variance) > 1 ? 'text-red-600 font-bold' : 'text-slate-400'}`} title="Generated minus (Claimed + Recovered + Rider Paid + Pending)">
                          {Math.abs(r.variance) < 0.01 ? '✓' : `AED ${Number(r.variance).toFixed(2)}`}
                        </TableCell>
                        {auditGroupBy === 'rider' && (
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 gap-1"
                              disabled={!r.rider_id || !r.month_key || r.month_key === 'Unknown'}
                              onClick={() => handleWhatsAppSalikSummary(r)}
                              title={r.rider_id ? 'Send Salik summary via WhatsApp' : 'Rider not identified on this row'}
                              data-testid={`salik-audit-whatsapp-${r.key}`}
                            >
                              <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-[11px] text-slate-500 leading-relaxed mt-2">
                  <strong>4-bucket classification</strong> (Generated = Claimed + Recovered + Rider Paid + Pending):
                  <span className="text-green-700"> • <strong>Recovered</strong></span> — recovered via approved payroll (Recovery Report signal).
                  <span className="text-teal-700"> • <strong>Paid by Rider</strong></span> — Mark-Paid-With-Proof dialog used.
                  <span className="text-purple-700"> • <strong>Claimed to Lessee</strong></span> — external lessees only.
                  <span className="text-amber-700"> • <strong>Pending</strong></span> — everything else.
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ===== Mark Paid With Proof Dialog ===== */}
      <Dialog open={paidProofDialog.open} onOpenChange={(o) => { if (!o && !paidProofSubmitting) closePaidProofDialog(); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-xl'} max-h-[90vh] overflow-y-auto`} data-testid="salik-paid-proof-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              {paidProofDialog.existingProofUrl ? 'Replace SALIK Proof of Payment' : 'Mark SALIK as Paid — Proof Required'}
            </DialogTitle>
            <DialogDescription>
              {paidProofDialog.contextLabel}
              {paidProofDialog.mode === 'bulk' && ` — ${paidProofDialog.salikIds.length} record(s) will be updated`}
              {paidProofDialog.mode === 'lessee_month' && ' — all matching unpaid SALIK records in this period will be updated'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Payment Source <span className="text-red-500">*</span></Label>
              <select value={paidProofForm.payment_source} onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_source: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="salik-paid-proof-source">
                <option value="rider_direct">Paid by Rider Directly</option>
                <option value="lessee_claim">Claimed to Lessee</option>
                <option value="salary_deduction">Deducted from Rider Salary</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Payment Reference No. <span className="text-red-500">*</span></Label>
                <Input type="text" value={paidProofForm.payment_ref_no} onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_ref_no: e.target.value })} placeholder="e.g. TXN-2026-04-0451" data-testid="salik-paid-proof-ref" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Date <span className="text-red-500">*</span></Label>
                  <Input type="date" value={paidProofForm.payment_date} onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_date: e.target.value })} data-testid="salik-paid-proof-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Time <span className="text-red-500">*</span></Label>
                  <Input type="time" value={paidProofForm.payment_time} onChange={(e) => setPaidProofForm({ ...paidProofForm, payment_time: e.target.value })} data-testid="salik-paid-proof-time" />
                </div>
              </div>
            </div>
            {paidProofDialog.existingProofUrl && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Existing Proof on File</p>
                <a href={paidProofDialog.existingProofUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline break-all">{paidProofDialog.existingProofUrl}</a>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-amber-800">Reason for replacing existing proof <span className="text-red-500">*</span></Label>
                  <Textarea rows={2} placeholder="Describe the reason for replacing the previously uploaded proof (min 5 characters)…" value={paidProofForm.replace_reason} onChange={(e) => setPaidProofForm({ ...paidProofForm, replace_reason: e.target.value })} data-testid="salik-paid-proof-replace-reason" />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{paidProofDialog.existingProofUrl ? 'New Proof File' : 'Proof of Payment'} <span className="text-red-500">*</span></Label>
              <Input type="file" accept="image/*,.pdf,application/pdf" onChange={(e) => setPaidProofForm({ ...paidProofForm, file: e.target.files[0] || null })} className="cursor-pointer" data-testid="salik-paid-proof-file" />
              <p className="text-[11px] text-slate-500">Accepted: JPG, PNG, WEBP, HEIC, PDF — max 10 MB</p>
              {paidProofForm.file && (
                <div className="mt-2 p-2 rounded border border-green-200 bg-green-50 text-xs flex items-center gap-2">
                  {paidProofForm.file.type.startsWith('image/') ? <Image className="w-4 h-4 text-green-700" /> : <FileText className="w-4 h-4 text-red-600" />}
                  <span className="font-medium truncate">{paidProofForm.file.name}</span>
                  <span className="text-slate-500 ml-auto">{(paidProofForm.file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Notes (optional)</Label>
              <Textarea rows={2} placeholder="Any additional context for the audit trail…" value={paidProofForm.notes} onChange={(e) => setPaidProofForm({ ...paidProofForm, notes: e.target.value })} data-testid="salik-paid-proof-notes" />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={closePaidProofDialog} disabled={paidProofSubmitting}>Cancel</Button>
              <Button onClick={submitPaidProofDialog} disabled={paidProofSubmitting} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700" data-testid="salik-paid-proof-submit">
                {paidProofSubmitting ? <><Loader className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {paidProofDialog.existingProofUrl ? 'Replace Proof' : 'Mark as Paid'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Proof History Viewer ===== */}
      <Dialog open={proofHistoryDialog.open} onOpenChange={(o) => { if (!o) setProofHistoryDialog({ open: false, salikId: '', data: null, loading: false }); }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[85vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> SALIK Proof of Payment — Audit Trail</DialogTitle>
            <DialogDescription>SALIK ID: {proofHistoryDialog.salikId?.slice(0, 8)}…</DialogDescription>
          </DialogHeader>
          {proofHistoryDialog.loading ? (
            <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin" /></div>
          ) : proofHistoryDialog.data && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-green-200 bg-green-50 p-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Source:</span> <span className="font-medium">{proofHistoryDialog.data.payment_source || '—'}</span></div>
                  <div><span className="text-slate-500">Ref #:</span> <span className="font-medium">{proofHistoryDialog.data.payment_ref_no || '—'}</span></div>
                  <div><span className="text-slate-500">Date:</span> <span className="font-medium">{proofHistoryDialog.data.payment_date || '—'}</span></div>
                  <div><span className="text-slate-500">Time:</span> <span className="font-medium">{proofHistoryDialog.data.payment_time || '—'}</span></div>
                </div>
                {proofHistoryDialog.data.current_proof_file_url && (
                  <div className="mt-2"><a href={proofHistoryDialog.data.current_proof_file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline break-all">View current proof file →</a></div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">History ({(proofHistoryDialog.data.history || []).length})</p>
                {(proofHistoryDialog.data.history || []).length === 0 ? (
                  <p className="text-xs text-slate-500 py-3 text-center">No history recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {proofHistoryDialog.data.history.map((h, i) => (
                      <div key={i} className="rounded border border-slate-200 p-2 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold capitalize text-slate-700">{(h.action || '').replace('_', ' ')}</span>
                          <span className="text-slate-500">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                        </div>
                        <div className="text-slate-600">by {h.by || 'system'}</div>
                        {h.action === 'replace_proof' ? (
                          <div className="mt-1 space-y-0.5 text-[11px]">
                            <div className="text-amber-700"><strong>Reason:</strong> {h.replace_reason || '—'}</div>
                            <div>Previous: <a href={h.previous_proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">{h.previous_proof_file_url || '—'}</a></div>
                            <div>New: <a href={h.new_proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">{h.new_proof_file_url || '—'}</a></div>
                          </div>
                        ) : (
                          <div className="mt-1 text-[11px] text-slate-600">
                            <div>Ref #{h.payment_ref_no} — {h.payment_source} — {h.payment_date} {h.payment_time}</div>
                            {h.proof_file_url && <a href={h.proof_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline break-all">View file →</a>}
                            {h.notes && <div className="mt-0.5 text-slate-500"><em>Note: {h.notes}</em></div>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Bike Ranking Dialog */}
      <Dialog open={showBikeRankingDialog} onOpenChange={(open) => {
        setShowBikeRankingDialog(open);
        if (!open) {
          // Reset to all-time when closing
          setRankingPeriod('all');
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              SALIK Usage Ranking
            </DialogTitle>
            <DialogDescription>
              Motorcycles ranked by total SALIK toll charges (highest to lowest)
            </DialogDescription>
          </DialogHeader>
          
          {/* Period Filter */}
          <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium whitespace-nowrap">Period:</Label>
              <Select value={rankingPeriod} onValueChange={setRankingPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="month">By Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {rankingPeriod === 'month' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Month:</Label>
                  <Select value={String(rankingMonth)} onValueChange={(v) => setRankingMonth(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                        <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Year:</Label>
                  <Select value={String(rankingYear)} onValueChange={(v) => setRankingYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          {/* Filtered Rankings */}
          {(() => {
            // Calculate filtered bike usage based on selected period
            let filteredRecords = salikRecords;
            let periodLabel = 'All Time';
            
            if (rankingPeriod === 'month') {
              const startDate = new Date(rankingYear, rankingMonth, 1);
              const endDate = new Date(rankingYear, rankingMonth + 1, 0, 23, 59, 59);
              periodLabel = `${['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'][rankingMonth]} ${rankingYear}`;
              
              filteredRecords = salikRecords.filter(r => {
                const recordDate = new Date(r.salik_date || r.date);
                return recordDate >= startDate && recordDate <= endDate;
              });
            }
            
            // Calculate bike usage for filtered records
            const filteredBikeUsage = {};
            filteredRecords.forEach(r => {
              const plate = r.bike_plate_number || r.motorcycle_plate || 'Unknown';
              const lesseeName = r.lessee_name || r.lessee_company_name || '-';
              const riderName = r.rider_name || '-';
              if (!filteredBikeUsage[plate]) {
                filteredBikeUsage[plate] = { plate, count: 0, total: 0, lessee: lesseeName, rider: riderName };
              }
              filteredBikeUsage[plate].count += 1;
              filteredBikeUsage[plate].total += (r.total_amount || (r.amount || 0) * 1.15);
              if (lesseeName !== '-') filteredBikeUsage[plate].lessee = lesseeName;
              if (riderName !== '-') filteredBikeUsage[plate].rider = riderName;
            });
            
            const sortedFilteredBikeUsage = Object.values(filteredBikeUsage).sort((a, b) => b.total - a.total);
            
            return (
              <div className="mt-2">
                <div className="text-sm text-slate-500 mb-3">
                  Showing: <strong>{periodLabel}</strong> • {filteredRecords.length} record(s) • {sortedFilteredBikeUsage.length} motorcycle(s)
                </div>
                
                {sortedFilteredBikeUsage.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No SALIK records found for {periodLabel}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <div 
                      className="table-scroll-container"
                      style={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch'
                      }}
                    >
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12 font-semibold">Rank</TableHead>
                        <TableHead className="font-semibold">Plate Number</TableHead>
                        <TableHead className="font-semibold">Lessee</TableHead>
                        <TableHead className="font-semibold">Rider</TableHead>
                        <TableHead className="font-semibold text-center">Trips</TableHead>
                        <TableHead className="font-semibold text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedFilteredBikeUsage.map((bike, index) => (
                        <TableRow key={bike.plate} className={index === 0 ? 'bg-purple-50' : ''}>
                          <TableCell>
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              index === 0 ? 'bg-purple-600 text-white' : 
                              index === 1 ? 'bg-purple-400 text-white' : 
                              index === 2 ? 'bg-purple-300 text-purple-800' : 
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {index + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{bike.plate}</TableCell>
                          <TableCell className="text-sm text-slate-600">{bike.lessee || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{bike.rider || '-'}</TableCell>
                          <TableCell className="text-center">{bike.count}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {bike.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Salik Records ({sortedRecords.length})</CardTitle>
            {user?.role !== 'Account Manager' && (
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="download-salik-btn">
                      <Download className="w-4 h-4" />
                      {!isMobile && `Download (${sortedRecords.length})`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <div className="px-2 py-1.5 text-[11px] text-slate-500 border-b border-slate-100">
                      {buildSalikFilterInfo()}
                    </div>
                    <DropdownMenuItem onClick={() => handleDownloadSalik('excel')} className="cursor-pointer" data-testid="download-salik-excel">
                      <Download className="mr-2 h-4 w-4 text-green-600" />
                      Download Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownloadSalik('pdf')} className="cursor-pointer" data-testid="download-salik-pdf">
                      <Download className="mr-2 h-4 w-4 text-red-600" />
                      Download PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="w-4 h-4" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
                <Button
                  variant="default"
                  onClick={() => setTollReportDialogOpen(true)}
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  <Upload className="w-4 h-4" />
                  Import Toll Report
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showFilters && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
              <DateRangeFilter
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onFromDateChange={(val) => setFilters({ ...filters, fromDate: val })}
                onToDateChange={(val) => setFilters({ ...filters, toDate: val })}
              />
              <SearchFilters
                lessees={lessees}
                riders={riders}
                motorcycles={motorcycles}
                selectedLessee={filters.lessee}
                selectedRider={filters.rider}
                selectedBike={filters.bike}
                onLesseeChange={(val) => setFilters({ ...filters, lessee: val })}
                onRiderChange={(val) => setFilters({ ...filters, rider: val })}
                onBikeChange={(val) => setFilters({ ...filters, bike: val })}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '' })}>
                  Clear Filters
                </Button>
                {permissions.canEdit && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleBackfillNames}
                    className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Refresh Names
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* Bulk Actions Bar */}
          {selectedRecords.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm text-blue-800 font-medium">
                {selectedRecords.length} record(s) selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('Paid')}
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                >
                  Mark as Paid
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('Unpaid')}
                  className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                >
                  Mark as Unpaid
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('Recovered from Salary')}
                  className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  Recovered from Salary
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedRecords([])}
                  className="text-slate-600"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}
          
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 border-b border-slate-200">
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={sortedRecords.length > 0 && selectedRecords.length === sortedRecords.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700">Transaction ID</TableHead>
                  <TableHead className="font-semibold text-slate-700">
                    <div className="flex items-center gap-1">
                      Date & Time
                      <SortButton onSort={handleSort} currentSort={sortConfig} field="salik_date" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700">Plate Number</TableHead>
                  <TableHead className="font-semibold text-slate-700">Lessee Company</TableHead>
                  <TableHead className="font-semibold text-slate-700">Rider Name</TableHead>
                  <TableHead className="font-semibold text-slate-700">Toll Gate</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">SALIK (Dh)</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Admin (Dh)</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Total (Dh)</TableHead>
                  <TableHead className="font-semibold text-slate-700">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-slate-500 py-12">
                      <div className="space-y-2">
                        <Search className="w-8 h-8 mx-auto text-slate-400" />
                        <div>No Salik records found. Add your first record to get started.</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRecords.map((record, index) => (
                    <TableRow 
                      key={record.id}
                      className={`cursor-pointer hover:bg-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} ${selectedRecords.includes(record.id) ? 'bg-blue-50' : ''}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={() => toggleRecordSelection(record.id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(record)}>
                        <span className="text-sm font-mono text-slate-600">
                          {record.transaction_id || '-'}
                        </span>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(record)}>
                        <div>
                          <div className="font-medium text-slate-800">{formatDateDisplay(record.salik_date || record.date) || '-'}</div>
                          <div className="text-xs text-slate-500">{record.salik_time || record.time || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(record)} className="font-medium text-slate-800">{record.bike_plate_number || record.motorcycle_plate || '-'}</TableCell>
                      <TableCell onClick={() => handleViewDetails(record)} className="text-slate-600">{record.lessee_name || record.lessee_company_name || '-'}</TableCell>
                      <TableCell onClick={() => handleViewDetails(record)} className="text-slate-600 max-w-[140px]">
                        <div className="leading-tight text-xs">
                          <div className="truncate" title={record.current_rider_name || '-'}>
                            <span className="text-slate-400">Current: </span>{record.current_rider_name || '-'}
                          </div>
                          <div className="truncate text-slate-500" title={record.salik_by_rider_name || '-'}>
                            <span className="text-slate-400">SALIK by: </span>{record.salik_by_rider_name || '-'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(record)} className="text-slate-600">
                        <div className="flex items-center gap-1.5">
                          {record.is_parking && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 ring-1 ring-amber-200" title="Parking through Salik">P</span>
                          )}
                          <span>{record.is_parking ? (record.parking_location || record.salik_gate_name || record.gate_name || '-') : (record.salik_gate_name || record.gate_name || '-')}</span>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetails(record)} className="text-right font-medium text-slate-700">
                        {(record.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-600">
                        {(record.admin_charge || (record.amount || 0) * 0.15).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-slate-800">
                        {(record.total_amount || ((record.amount || 0) * 1.15)).toFixed(2)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select 
                          value={record.status || 'Unpaid'} 
                          onValueChange={(value) => handleStatusChange(record.id, value)}
                        >
                          <SelectTrigger className={`w-28 h-8 text-xs font-medium ${
                            record.status === 'Paid' 
                              ? 'bg-green-100 text-green-700 border-green-300' 
                              : record.status === 'Recovered from Salary'
                              ? 'bg-blue-100 text-blue-700 border-blue-300'
                              : 'bg-red-100 text-red-700 border-red-300'
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Unpaid" className="text-red-700">Unpaid</SelectItem>
                            <SelectItem value="Paid" className="text-green-700">Paid</SelectItem>
                            <SelectItem value="Recovered from Salary" className="text-blue-700">Recovered</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Salik Record Details</DialogTitle>
            <DialogDescription>
              View complete details of this Salik toll transaction
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Date</Label>
                  <p className="font-medium">{formatDateDisplay(selectedRecord.salik_date || selectedRecord.date) || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Time</Label>
                  <p className="font-medium">{selectedRecord.salik_time || selectedRecord.time || '-'}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-600">Motorcycle Plate Number</Label>
                <p className="font-medium text-lg">{selectedRecord.bike_plate_number || selectedRecord.motorcycle_plate || '-'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">Salik Tag Number</Label>
                <p className="font-medium">
                  {(() => {
                    const motorcycle = motorcycles.find(m => 
                      m.plate_number === selectedRecord.bike_plate_number || 
                      m.plate_number === selectedRecord.motorcycle_plate
                    );
                    return motorcycle?.salik_tag_no || '-';
                  })()}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">Lessee Company</Label>
                <p className="font-medium">{selectedRecord.lessee_name || selectedRecord.lessee_company_name || '-'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">Current Rider</Label>
                <p className="font-medium">{selectedRecord.current_rider_name || '-'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">SALIK by</Label>
                <p className="font-medium">{selectedRecord.salik_by_rider_name || '-'}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">Salik Gate</Label>
                <p className="font-medium">{selectedRecord.salik_gate_name || selectedRecord.gate_name || '-'}</p>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Salik Amount:</span>
                  <span className="font-semibold text-lg"><CurrencyDisplay amount={selectedRecord.amount || 0} /></span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Admin Charge:</span>
                  <span className="font-semibold text-orange-600"><CurrencyDisplay amount={selectedRecord.admin_charge || 0} /></span>
                </div>
                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-slate-700 font-semibold">Total Amount:</span>
                  <span className="font-bold text-2xl text-lime-700"><CurrencyDisplay amount={(selectedRecord.amount || 0) + (selectedRecord.admin_charge || 0)} /></span>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Salik;
